import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EnvironmentsService, IOcbEnvironment } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-profile',
    styleUrls: ['./profile.component.scss'],
    templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit {
    @ViewChild('profileForm')
    public profileForm: DxFormComponent;

    public loading: boolean;
    public environment: IOcbEnvironment & { enabled: boolean };

    public environmentEnabled: boolean = false;

    private environmentId: string = '';

    constructor(
        private environmentsService: EnvironmentsService,
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService) {
    }

    public ngOnInit() {
        this.route.params.subscribe(_ => {
            this.loading = true;
            this.environmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.environmentId)
                .pipe(finalize(() => this.loading = false))
                .subscribe(
                    res => this.environment = {
                        ...res,
                        enabled: !res.isDisabled,
                    },
                    err => this.handleError(err),
                );
        });
    }

    public toggleEnvironment(e) {
        const env = this.profileForm.formData as IOcbEnvironment;
        env.isDisabled = !e.value;
    }

    public onClickSave() {
        this.loading = true;

        this.environment.isDisabled = !this.environment.enabled;
        this.environmentsService.updateEnvironment(this.environmentId, this.environment)
            .pipe(
                mergeMap(_ => this.translateService.get('profile.msgUpdateComplete')),
                finalize(() => this.loading = false),
            )
            .subscribe(
                message => notify(message, 'success', 3000),
                err => this.handleError(err),
            );
    }

    public onClickDelete() {
        forkJoin(
            this.translateService.get('profile.msgDeleteConfirm', { name: this.environment.name }),
            this.translateService.get('profile.msgDeleteConfirmTitle'),
        ).pipe(
            mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
            filter(accepted => accepted),
            tap(_ => this.loading = true),
            mergeMap(_ => this.environmentsService.deleteEnvironment(this.environmentId)),
            mergeMap(_ => this.translateService.get('profile.msgDeleteComplete')),
            finalize(() => this.loading = false),
        )
            .subscribe(
                message => {
                    this.router.navigate(['environments']);
                    notify(message, 'success', 3000);
                },
                err => this.handleError(err),
            );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any) {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            }
            else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            }
        }
        else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
